// components/ResourceDetailModal.js
import React from 'react';

function formatDate(value) {
  if (!value) return '';
  try {
    // Support both Date objects and ISO strings
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) {
      return String(value);
    }
    // Simple, human-friendly date without time
    return d.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return String(value);
  }
}

export default function ResourceDetailModal({ item, isOpen, onClose }) {
  if (!isOpen || !item) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDownload = () => {
    const url = item.downloadUrl || item.file || item.url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const grades = item.grades || [];
  const tags = item.tags || [];

  return (
    <div
      className="fixed inset-0 z-modal flex items-start justify-center bg-black/50 backdrop-blur-sm px-2 sm:px-4 py-6 overflow-y-auto"
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
    >
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-elevated border border-gray-100 overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 left-3 z-10 p-1.5 rounded-full bg-white/80 hover:bg-white shadow-sm border border-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="סגור"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-gray-100 bg-gradient-to-l from-white to-blue-50/40">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-gray-500 hover:text-blue-600 mb-4 text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            חזרה
          </button>

          <div className="flex flex-wrap gap-2 mb-4">
            {item.subjectLabel && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs sm:text-sm font-medium">
                {item.subjectLabel}
              </span>
            )}
            {item.typeLabel && (
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs sm:text-sm font-medium">
                {item.typeLabel}
              </span>
            )}
            {grades.map((g) => (
              <span
                key={g}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs sm:text-sm font-medium"
              >
                כיתה {g}
              </span>
            ))}
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {item.title}
          </h1>
        </div>

        {/* Body */}
        <div className="grid md:grid-cols-3 gap-0">
          {/* Main */}
          <div className="md:col-span-2 p-6 sm:p-8 space-y-4">
            <h3 className="font-bold text-lg sm:text-xl mb-2 flex items-center gap-2 text-gray-900">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              תיאור המשאב
            </h3>

            <div className="prose max-w-none text-gray-700 text-sm sm:text-base space-y-3">
              {item.explanation && (
                <p>{item.explanation}</p>
              )}
              {!item.explanation && item.summary && (
                <p>{item.summary}</p>
              )}
              {item.author && (
                <p className="text-gray-500 text-sm">
                  מחבר: <span className="font-medium text-gray-700">{item.author}</span>
                </p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1 bg-gray-50 p-6 sm:p-8 border-r border-gray-100">
            <div className="space-y-4">
              <button
                onClick={handleDownload}
                className="w-full py-2.5 sm:py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-sm transition-colors flex items-center justify-center gap-2 text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!item.downloadUrl && !item.file && !item.url}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                </svg>
                מעבר למשאב
              </button>

              <div className="mt-4 border-t border-gray-200 pt-4 text-xs sm:text-sm text-gray-600 space-y-2">
                {item.date && (
                  <div className="flex justify-between gap-2">
                    <span>עודכן:</span>
                    <span className="font-medium">{formatDate(item.date)}</span>
                  </div>
                )}
                {item.subjectLabel && (
                  <div className="flex justify-between gap-2">
                    <span>מקצוע:</span>
                    <span className="font-medium">{item.subjectLabel}</span>
                  </div>
                )}
                {(item.typeLabel || item.model_type) && (
                  <div className="flex justify-between gap-2">
                    <span>סוג משאב:</span>
                    <span className="font-medium">{item.typeLabel || item.model_type}</span>
                  </div>
                )}
                {grades.length > 0 && (
                  <div className="flex justify-between gap-2">
                    <span>כיתות:</span>
                    <span className="font-medium">
                      {grades.join(', ')}
                    </span>
                  </div>
                )}
              </div>

              {tags.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-bold text-gray-900 mb-2 text-xs sm:text-sm uppercase tracking-wide">
                    תגיות
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs bg-white border border-gray-200 px-2 py-1 rounded text-gray-600"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


